"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import ThemeToggle from "@/components/ThemeToggle";
import LangToggle from "@/components/LangToggle";
import ChatWidget from "@/components/landing/ChatWidget";
import EmployeeCard, { type EmployeeCardData } from "@/components/landing/EmployeeCard";
import AgentBrainMap from "@/components/landing/AgentBrainMap";
import AnimatedNumber from "@/components/landing/AnimatedNumber";
import InViewFill from "@/components/landing/InViewFill";
import ScheduleTimeline from "@/components/landing/ScheduleTimeline";
import ToolsMarquee from "@/components/landing/ToolsMarquee";
import VoiceWidget from "@/components/landing/VoiceWidget";
import SkillMarketplace from "@/components/landing/SkillMarketplace";
import ModelShowcase from "@/components/landing/ModelShowcase";
import LeadCaptureButton from "@/components/landing/LeadCaptureButton";
import { useLandingLang } from "@/lib/landingI18n";
import { trackEvent } from "@/lib/analyticsClient";
import {
  Target,
  Sparkles,
  CalendarClock,
  Plug,
  Gauge,
  Megaphone,
  Handshake,
  Wallet,
  Headphones,
  BarChart3,
  ArrowRight,
  Mail,
  Calculator,
  Receipt,
  MessageSquare,
  Globe,
  ShieldCheck,
  Lock,
  Server,
  KeyRound,
  ShieldAlert,
  DatabaseZap,
  ClipboardCheck,
  Menu,
  X,
} from "lucide-react";
import {
  SiNotion,
  SiGmail,
  SiGooglesheets,
  SiTrello,
  SiZapier,
  SiHubspot,
  SiGithub,
  SiWhatsapp,
  SiFacebook,
  SiInstagram,
  SiMailchimp,
} from "react-icons/si";

// Language-agnostic "shape" data (icons, images, numbers) — text content is merged
// in at render time from the current language's dictionary (lib/landingI18n.ts).
const EMPLOYEE_SHAPE = [
  {
    avatarSrc: "/avatars/marlaa.png",
    roleIcon: <Megaphone size={17} />,
    toolIcons: [<SiGooglesheets key="s" size={12} />, <SiFacebook key="f" size={12} />, <SiHubspot key="h" size={12} />],
    allToolIcons: [
      <SiGooglesheets key="s" size={13} />,
      <SiFacebook key="f" size={13} />,
      <SiHubspot key="h" size={13} />,
      <SiInstagram key="i" size={13} />,
      <SiMailchimp key="m" size={13} />,
    ],
    statValues: ["72%", "68%", "142"],
    progress: 72,
  },
  {
    avatarSrc: "/avatars/bataa.png",
    roleIcon: <Handshake size={17} />,
    toolIcons: [<SiHubspot key="h" size={12} />, <MessageSquare key="sms" size={12} />, <Mail key="mail" size={12} />],
    allToolIcons: [
      <SiHubspot key="h" size={13} />,
      <MessageSquare key="sms" size={13} />,
      <Mail key="mail" size={13} />,
      <Headphones key="chat" size={13} />,
      <Globe key="web" size={13} />,
    ],
    statValues: ["85%", "80%", "37"],
    progress: 85,
  },
  {
    avatarSrc: "/avatars/saraa.png",
    roleIcon: <Wallet size={17} />,
    toolIcons: [<SiGooglesheets key="s" size={12} />, <BarChart3 key="r" size={12} />, <Wallet key="cf" size={12} />],
    allToolIcons: [
      <SiGooglesheets key="s" size={13} />,
      <BarChart3 key="r" size={13} />,
      <Wallet key="cf" size={13} />,
      <Calculator key="calc" size={13} />,
      <Receipt key="inv" size={13} />,
    ],
    statValues: ["90%", "87%", "64"],
    progress: 90,
  },
];

const HOW_IT_WORKS_SHAPE = [
  { icon: Target, widget: <InViewFill percent={82} /> },
  {
    icon: Sparkles,
    widget: (
      <div className="flex flex-wrap gap-1.5">
        {["SEO", "CRM", "Excel"].map((s) => (
          <span
            key={s}
            className="rounded-full border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-2 py-0.5 text-[11px] text-[var(--af-muted)]"
          >
            {s}
          </span>
        ))}
      </div>
    ),
  },
  { icon: CalendarClock, widget: <ScheduleTimeline slots={["09:00", "13:00", "18:00", "24:00"]} /> },
  {
    icon: Plug,
    widget: (
      <ToolsMarquee
        size="sm"
        slow
        tools={[
          { icon: <SiNotion />, label: "Notion" },
          { icon: <SiGmail />, label: "Gmail" },
          { icon: <SiTrello />, label: "Trello" },
          { icon: <SiZapier />, label: "Zapier" },
        ]}
      />
    ),
  },
  {
    icon: Gauge,
    widget: (
      <div className="text-2xl font-semibold text-[var(--af-accent)]">
        <AnimatedNumber value={98} suffix="%" />
      </div>
    ),
  },
];

const SECURITY_SHAPE = [Lock, ShieldCheck, KeyRound, ShieldAlert, DatabaseZap, ClipboardCheck];

const TOOLS = [
  { icon: <SiNotion />, label: "Notion" },
  { icon: <SiGmail />, label: "Gmail" },
  { icon: <SiGooglesheets />, label: "Google Sheets" },
  { icon: <SiTrello />, label: "Trello" },
  { icon: <SiZapier />, label: "Zapier" },
  { icon: <SiHubspot />, label: "HubSpot" },
  { icon: <SiGithub />, label: "GitHub" },
  { icon: <SiWhatsapp />, label: "WhatsApp" },
];

export default function Page() {
  const { L } = useLandingLang();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const employees: EmployeeCardData[] = EMPLOYEE_SHAPE.map((shape, i) => {
    const content = L.employees[i];
    return {
      name: content.name,
      role: content.role,
      avatarSrc: shape.avatarSrc,
      roleIcon: shape.roleIcon,
      goal: content.goal,
      goals: content.goals,
      tasks: content.tasks,
      skills: content.skills,
      allSkills: content.allSkills,
      tools: shape.toolIcons.map((icon, j) => ({ icon, label: content.toolLabels[j] })),
      allTools: shape.allToolIcons.map((icon, j) => ({ icon, label: content.allToolLabels[j] })),
      stats: shape.statValues.map((value, j) => ({ label: content.statLabels[j], value })),
      schedule: content.schedule,
      progress: shape.progress,
    };
  });

  return (
    <div className="af-root min-h-screen overflow-x-hidden">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-40 af-glass">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link href="/" className="flex min-w-0 shrink-0 items-center">
            <Image src="/logo-full.png" alt="agenticforce" width={2048} height={684} priority className="h-6 w-auto object-contain sm:h-7" />
          </Link>
          <nav className="hidden items-center gap-8 text-sm text-[var(--af-muted)] md:flex">
            <a className="transition hover:text-[var(--af-text)]" href="#how">{L.nav.how}</a>
            <a className="transition hover:text-[var(--af-text)]" href="#employees">{L.nav.employees}</a>
            <a className="transition hover:text-[var(--af-text)]" href="#security">{L.nav.security}</a>
            <a className="transition hover:text-[var(--af-text)]" href="#contact">{L.nav.contact}</a>
          </nav>
          <div className="flex min-w-0 items-center gap-2">
            <Link href="/demo" className="hidden text-sm text-[var(--af-muted)] transition hover:text-[var(--af-text)] sm:inline">
              {L.nav.demo}
            </Link>
            <LangToggle className="hidden sm:block" />
            <ThemeToggle className="hidden sm:flex" />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--af-border)] text-[var(--af-muted)] transition hover:text-[var(--af-text)] md:hidden"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <button
              type="button"
              aria-label="Close menu"
              className="absolute inset-0 bg-black/35"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="absolute right-0 top-0 flex h-dvh w-[min(82vw,320px)] flex-col border-l border-[var(--af-border)] bg-[var(--af-bg)] p-5 shadow-2xl">
              <div className="flex items-center justify-between">
                <Image src="/logo-full.png" alt="agenticforce" width={2048} height={684} className="h-6 w-auto object-contain" />
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--af-border)] text-[var(--af-muted)]"
                >
                  <X size={18} />
                </button>
              </div>
              <nav className="mt-8 flex flex-col gap-2 text-base font-medium text-[var(--af-text)]">
                {[
                  { href: "#how", label: L.nav.how },
                  { href: "#employees", label: L.nav.employees },
                  { href: "#security", label: L.nav.security },
                  { href: "#contact", label: L.nav.contact },
                  { href: "/demo", label: L.nav.demo },
                ].map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-xl px-3 py-3 transition hover:bg-[var(--af-bg-soft)]"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
              <div className="mt-auto flex items-center gap-3 border-t border-[var(--af-border)] pt-5">
                <LangToggle menuPlacement="top" />
                <ThemeToggle />
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="af-glow relative overflow-hidden">
        <div className="mx-auto max-w-4xl px-4 pt-16 pb-10 text-center sm:px-6 sm:pt-24">
          <div className="af-fade-in mx-auto mb-6 inline-flex max-w-full items-center gap-2 rounded-full border border-[var(--af-border)] bg-[var(--af-bg-soft)] px-3 py-1.5 text-[12px] text-[var(--af-muted)] sm:mb-8 sm:px-4 sm:text-[13px]">
            <span className="af-pulse-dot h-1.5 w-1.5 rounded-full bg-[#4ade80]" />
            <span className="truncate">{L.hero.badge}</span>
          </div>
          <h1 className="af-fade-in mx-auto max-w-[20rem] break-words text-[2rem] font-semibold leading-[1.08] tracking-tight sm:max-w-none sm:text-6xl lg:text-[4.2rem]">
            {L.hero.titlePre}{" "}
            <span className="text-[var(--af-accent)]">{L.hero.titleAccent}</span>
          </h1>
          <p className="af-fade-in mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[var(--af-muted)] sm:mt-6 sm:text-xl">
            {L.hero.sub}
          </p>
          <div className="af-fade-in mt-8 flex flex-col items-stretch justify-center gap-3 sm:mt-10 sm:flex-row sm:items-center">
            <Link
              href="/chat"
              onClick={() => trackEvent("cta_click", { cta: "hero_primary" })}
              className="group inline-flex min-w-0 items-center justify-center gap-2 whitespace-normal rounded-full bg-[var(--af-accent)] px-6 py-3.5 text-center text-base font-semibold text-black transition hover:bg-[var(--af-accent-hover)] sm:px-7"
            >
              {L.hero.ctaPrimary}
              <ArrowRight size={18} className="transition group-hover:translate-x-1" />
            </Link>
            <Link
              href="/demo"
              onClick={() => trackEvent("cta_click", { cta: "hero_demo" })}
              className="rounded-full border border-[var(--af-border)] bg-[var(--af-glass)] px-6 py-3.5 text-base font-medium text-[var(--af-text)] backdrop-blur transition hover:border-[var(--af-accent)] sm:px-7"
            >
              {L.hero.ctaSecondary}
            </Link>
          </div>

          {/* Metrics */}
          <div className="af-fade-in mt-10 grid grid-cols-2 gap-x-6 gap-y-6 sm:flex sm:items-center sm:justify-center sm:gap-10">
            <div>
              <div className="af-nums text-2xl font-semibold sm:text-3xl">24/7</div>
              <div className="mt-1 text-[13px] text-[var(--af-muted)]">{L.hero.metricWorking}</div>
            </div>
            <div className="hidden h-8 w-px bg-[var(--af-border)] sm:block" />
            <div>
              <div className="text-2xl font-semibold sm:text-3xl">
                <AnimatedNumber value={300} suffix="+" />
              </div>
              <div className="mt-1 text-[13px] text-[var(--af-muted)]">{L.hero.metricSkills}</div>
            </div>
            <div className="hidden h-8 w-px bg-[var(--af-border)] sm:block" />
            <div>
              <div className="text-2xl font-semibold sm:text-3xl">
                <AnimatedNumber value={500} suffix="+" />
              </div>
              <div className="mt-1 text-[13px] text-[var(--af-muted)]">{L.hero.metricConnections}</div>
            </div>
            <div className="hidden h-8 w-px bg-[var(--af-border)] sm:block" />
            <div>
              <div className="af-nums text-2xl font-semibold sm:text-3xl">{L.hero.metricPricingValue}</div>
              <div className="mt-1 text-[13px] text-[var(--af-muted)]">{L.hero.metricPricingSuffix}</div>
            </div>
          </div>
        </div>

        {/* Three digital employees */}
        <div id="employees" className="mx-auto max-w-5xl px-6 pb-4">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {employees.map((employee) => (
              <EmployeeCard key={employee.name} {...employee} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Agent brain map ── */}
      <section className="border-t border-[var(--af-border)] bg-[var(--af-bg-soft)]">
        <div className="mx-auto max-w-5xl px-6 pt-6 pb-0 sm:pt-6 sm:pb-0">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">{L.brainMap.heading}</h2>
            <p className="mt-4 text-lg text-[var(--af-muted)]">{L.brainMap.sub}</p>
          </div>
          <div className="mt-6 -mb-8 sm:-mb-10">
            <AgentBrainMap centerName={L.brainMap.centerName} labels={L.brainMap.nodeLabels} />
          </div>
        </div>
      </section>

      {/* ── Virtual office live preview ── */}
      <section className="border-t border-[var(--af-border)] bg-[var(--af-bg-soft)]">
        <div className="mx-auto max-w-5xl px-6 pt-8 pb-4 sm:pt-8 sm:pb-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">{L.office.heading}</h2>
            <p className="mt-4 text-lg text-[var(--af-muted)]">{L.office.sub}</p>
          </div>
          <div
            className="af-fade-in mx-auto mt-8 overflow-hidden rounded-[1.75rem] border border-[var(--af-border)] bg-[var(--af-panel)]"
            style={{ boxShadow: "var(--af-shadow)" }}
          >
            <div className="flex items-center gap-2 border-b border-[var(--af-border)] px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-[#ff5f56]" />
              <span className="h-3 w-3 rounded-full bg-[#ffbd2e]" />
              <span className="h-3 w-3 rounded-full bg-[#27c93f]" />
              <div className="ml-3 flex items-center gap-2 rounded-full bg-[var(--af-bg-soft)] px-3 py-1 text-xs text-[var(--af-muted)]">
                <span className="af-pulse-dot h-1.5 w-1.5 rounded-full bg-[#4ade80]" />
                {L.office.statusPill}
              </div>
              <Link href="/demo" className="ml-auto text-xs font-medium text-[var(--af-accent)] hover:opacity-80">
                {L.office.open}
              </Link>
            </div>
            <iframe src="/demo" title="agenticforce live demo" loading="lazy" className="block aspect-video w-full bg-[var(--pixel-bg)]" />
          </div>
        </div>
      </section>

      {/* ── How each digital employee works ── */}
      <section id="how">
        <div className="mx-auto max-w-6xl px-6 pt-6 pb-12 sm:pt-6 sm:pb-14">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">{L.howItWorks.heading}</h2>
            <p className="mt-4 text-lg text-[var(--af-muted)]">{L.howItWorks.sub}</p>
          </div>
          <div className="mt-10 grid gap-5 sm:mt-12 sm:grid-cols-2 lg:grid-cols-5">
            {HOW_IT_WORKS_SHAPE.map(({ icon: Icon, widget }, i) => {
              const content = L.howItWorks.items[i];
              return (
                <div
                  key={content.title}
                  className="af-lift rounded-3xl border border-[var(--af-border)] bg-[var(--af-bg)] p-6"
                  style={{ boxShadow: "var(--af-shadow)" }}
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--af-accent)]/12 text-[var(--af-accent)]">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-[15px] font-semibold">{content.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--af-muted)]">{content.desc}</p>
                  <div className="mt-5">{widget}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Skill marketplace ── */}
      <section className="border-t border-[var(--af-border)] bg-[var(--af-bg-soft)]">
        <div className="mx-auto max-w-5xl px-6 pt-8 pb-4 sm:pt-8 sm:pb-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">{L.skillMarketplace.heading}</h2>
            <p className="mt-4 text-lg text-[var(--af-muted)]">{L.skillMarketplace.sub}</p>
          </div>
          <div className="mt-8 sm:mt-10">
            <SkillMarketplace categoryLabels={L.skillMarketplace.categories} />
          </div>
        </div>
      </section>

      {/* ── Models used ── */}
      <section className="border-t border-[var(--af-border)] bg-[var(--af-bg)]">
        <div className="mx-auto max-w-6xl px-6 pt-8 pb-6 sm:pt-8 sm:pb-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mx-auto max-w-4xl break-words text-3xl font-semibold tracking-tight sm:text-5xl">{L.aiModels.heading}</h2>
            <p className="mx-auto mt-4 max-w-3xl text-lg text-[var(--af-muted)]">{L.aiModels.sub}</p>
          </div>
          <div className="mt-8 sm:mt-10">
            <ModelShowcase content={L.aiModels} />
          </div>
        </div>
      </section>

      {/* ── Security ── */}
      <section id="security" className="border-t border-[var(--af-border)]">
        <div className="mx-auto max-w-5xl px-6 pt-6 pb-16 sm:pt-6 sm:pb-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">{L.security.heading}</h2>
            <p className="mt-4 text-lg text-[var(--af-muted)]">{L.security.sub}</p>
          </div>

          {/* Server ownership banner */}
          <div
            className="af-fade-in mt-10 flex flex-col items-start gap-4 rounded-3xl border border-[var(--af-border)] bg-[var(--af-bg)] p-6 sm:flex-row sm:items-center sm:p-8"
            style={{ boxShadow: "var(--af-shadow)" }}
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--af-accent)]/12 text-[var(--af-accent)]">
              <Server size={24} />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold">{L.security.serverTitle}</h3>
              <p className="mt-1.5 text-[14px] leading-relaxed text-[var(--af-muted)]">{L.security.serverDesc}</p>
            </div>
          </div>

          {/* Security standards grid */}
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SECURITY_SHAPE.map((Icon, i) => {
              const content = L.security.standards[i];
              return (
                <div
                  key={content.title}
                  className="af-lift rounded-3xl border border-[var(--af-border)] bg-[var(--af-bg)] p-6"
                  style={{ boxShadow: "var(--af-shadow)" }}
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--af-accent)]/12 text-[var(--af-accent)]">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-[15px] font-semibold">{content.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--af-muted)]">{content.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Data protection statement */}
          <div
            className="af-fade-in mt-6 flex flex-col items-start gap-4 rounded-3xl border border-[var(--af-border)] bg-[var(--af-accent)]/8 p-6 sm:flex-row sm:items-center sm:p-8"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--af-accent)] text-black">
              <ShieldCheck size={24} />
            </div>
            <p className="text-[14px] leading-relaxed text-[var(--af-text)]">{L.security.dataStatement}</p>
          </div>
        </div>
      </section>

      {/* ── Connected tools strip ── */}
      <section className="border-t border-[var(--af-border)] bg-[var(--af-bg-soft)]">
        <div className="mx-auto max-w-5xl px-6 py-14 sm:py-16">
          <p className="mb-8 text-center text-sm text-[var(--af-muted)]">{L.toolsStrip.heading}</p>
          <ToolsMarquee tools={TOOLS} />
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section id="contact">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20 text-center">
          <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">{L.finalCta.heading}</h2>
          <p className="mt-4 text-lg text-[var(--af-muted)]">{L.finalCta.sub}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <LeadCaptureButton>{L.finalCta.cta}</LeadCaptureButton>
            <a
              href="mailto:hello@agenticforce.tech"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--af-border)] bg-[var(--af-bg)] px-7 py-3.5 text-base font-medium transition hover:border-[var(--af-accent)]"
            >
              <Mail size={18} /> hello@agenticforce.tech
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-[var(--af-border)]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-6 py-10 md:flex-row">
          <Image src="/logo-full.png" alt="agenticforce" width={2048} height={684} className="h-6 w-auto object-contain" />
          <p className="text-sm text-[var(--af-muted)]">
            © {new Date().getFullYear()} agenticforce — {L.footer.tagline}
          </p>
          <a
            href="mailto:hello@agenticforce.tech"
            className="text-sm text-[var(--af-muted)] transition hover:text-[var(--af-text)]"
          >
            hello@agenticforce.tech
          </a>
        </div>
      </footer>

      <VoiceWidget />
      <ChatWidget />
    </div>
  );
}
